import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Calendar, ChevronDown } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";
import ShareBar from "@/components/ShareBar";

// helper: extract H2 headings for table of contents
function extractTOC(md) {
  const lines = (md || "").split("\n");
  const items = [];
  lines.forEach(l => {
    const m = /^##\s+(.+)$/.exec(l);
    if (m) {
      const text = m[1].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      items.push({ id, text });
    }
  });
  return items;
}

export default function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/blog/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  // Update <title> and social meta tags dynamically (for OG scrapers)
  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    document.title = `${article.title} · Happy Home Agency`;

    const currentUrl = window.location.href;
    const heroImg = resolveMediaUrl(article.hero_image);
    const desc = article.meta_description || article.excerpt || "";

    const setNamedMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setPropMeta = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };

    setNamedMeta("description", desc);
    setNamedMeta("twitter:card", heroImg ? "summary_large_image" : "summary");
    setNamedMeta("twitter:title", article.title);
    setNamedMeta("twitter:description", desc);
    if (heroImg) setNamedMeta("twitter:image", heroImg);

    setPropMeta("og:type", "article");
    setPropMeta("og:site_name", "Happy Home Agency");
    setPropMeta("og:title", article.title);
    setPropMeta("og:description", desc);
    setPropMeta("og:url", currentUrl);
    if (heroImg) setPropMeta("og:image", heroImg);

    return () => { document.title = prevTitle; };
  }, [article]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500" data-testid="article-loading">Loading…</div>;
  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="article-not-found">
      <div className="font-serif text-4xl text-[#0A1128] mb-4">Article not found</div>
      <p className="text-gray-600 mb-8">This article may have been moved or unpublished.</p>
      <Link to="/blog" className="btn-outline">Back to Blog</Link>
    </div>
  );
  if (!article) return null;

  const toc = extractTOC(article.body);
  const publishedDate = article.published_at || article.updated_at;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description || article.excerpt,
    "image": article.hero_image ? [resolveMediaUrl(article.hero_image)] : [],
    "datePublished": publishedDate,
    "dateModified": article.updated_at,
    "author": { "@type": "Organization", "name": article.author_name || "Happy Home Agency" },
    "publisher": {
      "@type": "Organization",
      "name": "Happy Home Agency",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_notes-to-site/artifacts/cver0rlc_HappyHomeAgency%20Logo2026.png"
      }
    },
    "keywords": (article.tags || []).join(", "),
  };
  const faqJsonLd = (article.faq && article.faq.length > 0) ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faq.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    })),
  } : null;

  return (
    <article data-testid="blog-article-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      {faqJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />}

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-8 text-xs tracking-widest uppercase text-gray-500">
        <Link to="/blog" className="hover:text-[#688A4E] flex items-center gap-2 w-fit" data-testid="article-breadcrumb"><ArrowLeft className="w-3 h-3"/> Blog</Link>
      </div>

      {/* Hero */}
      <header className="max-w-4xl mx-auto px-6 sm:px-10 pt-10 pb-12">
        <div className="flex flex-wrap gap-2 mb-6">
          {(article.tags || []).map((t) => <span key={t} className="text-xs border border-gray-300 px-2 py-1">{t}</span>)}
        </div>
        <h1 className="font-serif text-4xl sm:text-6xl leading-[1.05] text-[#0A1128] mb-8">{article.title}</h1>
        <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
          <span className="flex items-center gap-2"><Calendar className="w-4 h-4"/>{new Date(publishedDate).toLocaleDateString("en-SG", { year: "numeric", month: "long", day: "numeric" })}</span>
          <span>·</span>
          <span>By {article.author_name}</span>
          {article.view_count ? <><span>·</span><span>{article.view_count} views</span></> : null}
          {article.share_count ? <><span>·</span><span data-testid="header-share-count" className="text-[#688A4E]">{article.share_count} share{article.share_count === 1 ? "" : "s"}</span></> : null}
        </div>
      </header>

      {article.hero_image && (
        <div className="w-full max-w-6xl mx-auto px-6 sm:px-10 mb-16">
          <img src={resolveMediaUrl(article.hero_image)} alt={article.title} className="w-full h-[400px] sm:h-[540px] object-cover"/>
        </div>
      )}

      {/* Body layout */}
      <div className="max-w-6xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 pb-24">
        {toc.length > 0 && (
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28 border-l border-gray-200 pl-6">
              <div className="overline mb-4">Contents</div>
              <ul className="space-y-3 text-sm">
                {toc.map((item) => (
                  <li key={item.id}><a href={`#${item.id}`} className="text-gray-600 hover:text-[#688A4E]">{item.text}</a></li>
                ))}
              </ul>
            </div>
          </aside>
        )}
        <div className={toc.length > 0 ? "lg:col-span-9" : "lg:col-span-12 max-w-3xl mx-auto"}>
          {article.excerpt && (
            <div className="text-xl leading-relaxed text-gray-700 border-l-2 border-[#688A4E] pl-6 mb-12 font-serif italic">
              {article.excerpt}
            </div>
          )}
          <div className="prose prose-lg max-w-none" data-testid="article-body">
            <style>{`
              [data-testid="article-body"] h2 { font-family: 'Playfair Display', serif; font-size: 2.25rem; color: #0A1128; margin: 2.5rem 0 1rem; line-height: 1.15; scroll-margin-top: 6rem; }
              [data-testid="article-body"] h3 { font-family: 'Playfair Display', serif; font-size: 1.5rem; color: #0A1128; margin: 2rem 0 0.75rem; }
              [data-testid="article-body"] p { color: #374151; font-size: 1.0625rem; line-height: 1.8; margin: 1rem 0; }
              [data-testid="article-body"] a { color: #688A4E; text-decoration: underline; text-underline-offset: 3px; }
              [data-testid="article-body"] ul, [data-testid="article-body"] ol { color: #374151; margin: 1rem 0 1rem 1.5rem; }
              [data-testid="article-body"] li { margin: 0.5rem 0; line-height: 1.75; }
              [data-testid="article-body"] blockquote { border-left: 3px solid #688A4E; padding-left: 1.5rem; font-style: italic; color: #0A1128; margin: 1.5rem 0; }
              [data-testid="article-body"] code { background: #F9F9F7; padding: 0.15rem 0.4rem; font-size: 0.9em; color: #0A1128; }
              [data-testid="article-body"] pre { background: #0A1128; color: #fff; padding: 1.25rem; overflow-x: auto; margin: 1.5rem 0; }
              [data-testid="article-body"] pre code { background: transparent; color: #fff; }
              [data-testid="article-body"] img { max-width: 100%; margin: 2rem 0; }
              [data-testid="article-body"] strong { color: #0A1128; }
              [data-testid="article-body"] table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; }
              [data-testid="article-body"] th, [data-testid="article-body"] td { border: 1px solid #E5E7EB; padding: 0.75rem 1rem; text-align: left; }
              [data-testid="article-body"] th { background: #F9F9F7; }
            `}</style>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({ node, children, ...props }) => {
                  const text = String(children).trim();
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                  return <h2 id={id} {...props}>{children}</h2>;
                },
              }}
            >
              {article.body || ""}
            </ReactMarkdown>
          </div>

          {/* Share buttons */}
          <ShareBar
            title={article.title}
            url={typeof window !== "undefined" ? window.location.href : ""}
            description={article.meta_description || article.excerpt || ""}
            image={article.hero_image || ""}
            slug={article.slug}
            initialShareCount={article.share_count || 0}
          />

          {/* FAQ */}
          {article.faq && article.faq.length > 0 && (
            <section className="mt-16 border-t border-gray-200 pt-12" data-testid="article-faq">
              <div className="overline mb-4">Frequently Asked</div>
              <h2 className="font-serif text-3xl text-[#0A1128] mb-8">Questions & answers</h2>
              <div className="divide-y divide-gray-200">
                {article.faq.map((f, i) => (
                  <div key={i} className="py-5">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      data-testid={`article-faq-${i}`}
                      className="w-full flex items-center justify-between text-left group"
                    >
                      <span className="font-serif text-xl text-[#0A1128] group-hover:text-[#688A4E] transition pr-4">{f.question}</span>
                      <ChevronDown className={`w-5 h-5 text-[#688A4E] shrink-0 transition ${openFAQ === i ? "rotate-180" : ""}`}/>
                    </button>
                    {openFAQ === i && <div className="mt-3 text-gray-700 leading-relaxed">{f.answer}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Author + CTA */}
          <section className="mt-16 bg-[#0A1128] text-white px-8 sm:px-12 py-12 grain">
            <div className="overline text-[#688A4E] mb-4">Ready to find your helper?</div>
            <h3 className="font-serif text-3xl sm:text-4xl leading-tight mb-6">Let Happy Home Agency match your family with someone you can trust.</h3>
            <p className="text-[#A1A9BC] mb-8 leading-relaxed">Since 2006, we've helped Singapore families find the right helper — not just any helper. EA Licence 06C4290.</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/application" className="btn-primary" data-testid="article-cta-apply">Start Your Application <ArrowRight className="w-4 h-4"/></Link>
              <Link to="/contact" className="btn-outline-light" data-testid="article-cta-contact">Speak to a Consultant</Link>
            </div>
          </section>
        </div>
      </div>

      {/* Related */}
      {article.related && article.related.length > 0 && (
        <section className="bg-[#F9F9F7]" data-testid="article-related">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
            <div className="overline mb-4">Continue Reading</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#0A1128] mb-10">Related articles</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {article.related.map((r) => (
                <Link to={`/blog/${r.slug}`} key={r.id} className="group bg-white border border-gray-200 p-6 hover:shadow-xl transition">
                  <div className="font-serif text-xl text-[#0A1128] mb-3 group-hover:text-[#688A4E] transition">{r.title}</div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{r.excerpt}</p>
                  <div className="text-xs text-[#688A4E] flex items-center gap-1">Read <ArrowRight className="w-3.5 h-3.5"/></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
